import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AdvertisementCampaign } from '@/types';
import { useCommunityContext } from '@/providers/community-context';
import { CampaignForm } from './components/CampaignForm';
import type { CampaignFormValues } from './components/campaign-schema';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { postMultipartImageUpload } from '@/lib/vertex-upload';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle as CardHeaderTitle,
} from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import useSWR from 'swr';

export default function EditCampaignPage() {
  const navigate = useNavigate();
  const { community } = useCommunityContext();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading, isValidating, mutate } = useSWR(
    community?.id ? `/api/communities/${community.id}/advertisement-campaigns` : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );

  const toLocalISOString = (dateObj: Date) => {
    const tzOffset = dateObj.getTimezoneOffset() * 60000;
    return new Date(dateObj.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  const campaign = data?.campaigns?.find((c: AdvertisementCampaign) => c.id === campaignId);

  const initialValues = campaign
    ? {
        type: campaign.type,
        startDateTime: campaign.startDate ? toLocalISOString(new Date(campaign.startDate)) : '',
        endDateTime: campaign.endDate ? toLocalISOString(new Date(campaign.endDate)) : '',
      }
    : undefined;

  const onSubmit = async (formData: CampaignFormValues) => {
    if (!community?.id || !campaignId) return;

    try {
      setIsSubmitting(true);
      const payload: {
        type?: string;
        startDateTime?: string;
        endDateTime?: string;
        horizontalMediaUrl?: string;
        verticalMediaUrl?: string;
      } = {};
      if (formData.type) payload.type = formData.type;
      if (formData.startDateTime) {
        payload.startDateTime = new Date(formData.startDateTime).toISOString();
      }
      if (formData.endDateTime) {
        payload.endDateTime = new Date(formData.endDateTime).toISOString();
      }
      let horizontalMediaUrl: string | undefined;
      if (formData.horizontalMedia && formData.horizontalMedia instanceof File) {
        const uploadData = new FormData();
        uploadData.append('file', formData.horizontalMedia);
        uploadData.append('folder', 'advertisement-campaigns');
        const result = await postMultipartImageUpload(uploadData);
        horizontalMediaUrl = result.url;
      }

      let verticalMediaUrl: string | undefined;
      if (formData.verticalMedia && formData.verticalMedia instanceof File) {
        const uploadData = new FormData();
        uploadData.append('file', formData.verticalMedia);
        uploadData.append('folder', 'advertisement-campaigns');
        const result = await postMultipartImageUpload(uploadData);
        verticalMediaUrl = result.url;
      }

      if (horizontalMediaUrl) {
        payload.horizontalMediaUrl = horizontalMediaUrl;
      }
      if (verticalMediaUrl) {
        payload.verticalMediaUrl = verticalMediaUrl;
      }

      await axiosInstance.patch(
        `/api/communities/${community.id}/advertisement-campaigns/${campaignId}`,
        payload
      );

      toast.success('Campaign updated successfully');
      mutate();
      navigate(`/${community.id}/advertisement`);
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error('Failed to update campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || (isValidating && !campaign)) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) {
    return <div className="text-center p-12 text-muted-foreground">Campaign not found.</div>;
  }

  const ad = campaign.advertisement;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Card className="w-full border-0 bg-card shadow-none md:shadow-2xl animate-in slide-in-from-bottom-5 duration-300">
        <CardHeader className="px-3 md:px-6">
          <CardHeaderTitle className="text-2xl font-black tracking-tight text-foreground">
            Edit Campaign
          </CardHeaderTitle>
          <CardDescription className="text-muted-foreground">
            Update schedule and placement. Edit ad content from the advertisements list.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          <div className="mb-6 mx-6 flex gap-4 items-center rounded-xl border border-border/40 bg-muted/30 p-4">
            {campaign.horizontalMediaUrl || campaign.verticalMediaUrl ? (
              <img
                src={campaign.horizontalMediaUrl || campaign.verticalMediaUrl || ''}
                alt={ad.title}
                className="w-24 h-16 object-cover rounded-lg shrink-0"
              />
            ) : null}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Ad
              </p>
              <p className="font-semibold text-foreground">{ad.title}</p>
            </div>
          </div>
          <CampaignForm
            initialValues={initialValues}
            initialHorizontalMediaUrl={campaign.horizontalMediaUrl || undefined}
            initialVerticalMediaUrl={campaign.verticalMediaUrl || undefined}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onCancel={() => navigate(-1)}
            isEditing
          />
        </CardContent>
      </Card>
    </div>
  );
}
